import {ChangeDetectionStrategy, Component, ElementRef, forwardRef, Injector, Input} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {L10nTranslationService} from 'angular-l10n';
import {AccountService} from '../../../../components/accounts';
import {AccountType} from '../../../../enums/account-type';
import {isExchangeWithSplitAccount} from '../../../../enums/exchange';
import {DisplayExchangeAccountFactory} from '../../../../factories/display-exchange-account.factory';
import {ExchangeAccount, ExchangeAccountBase} from '../../../../models/exchange-account';
import {AccountAutocompleteComponent, AccountAutocompleteListItem} from './account-autocomplete.component';

interface AccountBaseAutocompleteListItem extends AccountAutocompleteListItem {
    accountType: AccountType;
}

@Component({
    selector: 'account-base-autocomplete',
    templateUrl: '../../../../components/autocomplete-input/autocomplete-input.component.html',
    styleUrls: ['../../../../components/autocomplete-input/autocomplete-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AccountBaseAutocompleteComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AccountBaseAutocompleteComponent
    extends AccountAutocompleteComponent<ExchangeAccountBase, AccountBaseAutocompleteListItem> {

    @Input()
    public supportSplitAccounts: boolean = false;

    constructor(
        elementRef: ElementRef,
        injector: Injector,
        protected override accountService: AccountService,
        private translationService: L10nTranslationService
    ) {
        super(elementRef, injector, accountService);
    }

    protected override outerValueToInnerValue(outerValue: ExchangeAccountBase): AccountBaseAutocompleteListItem {
        let resultItem: AccountBaseAutocompleteListItem = null;
        const account: ExchangeAccount = outerValue && this.accountService.getAccountByIdFromCache(outerValue.id);

        if (account) {
            let accountType: AccountType = account.type;

            if (this.supportSplitAccounts && isExchangeWithSplitAccount(account.exchange)) {
                accountType = outerValue.type;
            }

            resultItem = this.createItemWithCustomType(account, accountType);
        }

        return resultItem;
    }

    protected override innerValueToOuterValue(innerValue: AccountBaseAutocompleteListItem): ExchangeAccountBase {
        return innerValue
            ? {
                id:   innerValue.account.id,
                type: innerValue.accountType
            }
            : null;
    }

    protected override getAvailableList(): AccountBaseAutocompleteListItem[] {
        return super.getAvailableList()
            .reduce<AccountBaseAutocompleteListItem[]>(
                (acc, item) => {
                    if (this.supportSplitAccounts && isExchangeWithSplitAccount(item.account.exchange)) {
                        acc.push(
                            this.createItemWithCustomType(item.account, AccountType.CASH),
                            this.createItemWithCustomType(item.account, AccountType.MARGIN)
                        );
                    } else {
                        acc.push(item);
                    }

                    return acc;
                },
                []
            );
    }

    protected override createListItem(account: ExchangeAccount): AccountBaseAutocompleteListItem {
        return {
            ...super.createListItem(account),
            accountType: account.type
        };
    }

    private createItemWithCustomType(
        account: ExchangeAccount,
        accountType: AccountType
    ): AccountBaseAutocompleteListItem {
        const displayName = DisplayExchangeAccountFactory.getDisplayAccountName(
            account,
            true,
            accountType,
            this.translationService
        );

        return {
            account,
            accountType,
            displayName
        };
    }

}
